import React, { useEffect } from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag, CheckCircle } from 'lucide-react';
import { useCart } from '../../context/CartContext';

export function CartDrawer() {
  const { cart, isCartOpen, closeCart, updateItem, removeItem, itemCount } = useCart();

  // Lock body scroll while the drawer is open, and close on Escape.
  useEffect(() => {
    if (!isCartOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isCartOpen, closeCart]);

  const items = cart?.items || [];
  const subtotal = cart?.total || 0;

  const goTo = (path: string) => {
    closeCart();
    window.location.href = path;
  };

  const continueShopping = () => {
    const slug = cart?.vendorSlug;
    goTo(slug ? `/${slug}` : '/');
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={closeCart}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          opacity: isCartOpen ? 1 : 0,
          pointerEvents: isCartOpen ? 'auto' : 'none',
          transition: 'opacity 0.25s ease',
          zIndex: 2000,
        }}
        aria-hidden={!isCartOpen}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-label="Shopping cart"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100%',
          width: 'min(380px, 100vw)',
          background: '#fff',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          transform: isCartOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.28s ease',
          zIndex: 2001,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #eee' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={20} style={{ color: '#22c55e' }} />
            <strong style={{ fontSize: '1.05rem' }}>
              {itemCount > 0 ? `Added to cart (${itemCount})` : 'Your cart'}
            </strong>
          </div>
          <button
            onClick={closeCart}
            aria-label="Close cart"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: 4, lineHeight: 0 }}
          >
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {items.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#888', padding: '60px 0' }}>
              <ShoppingBag size={48} style={{ color: '#ccc', marginBottom: 12 }} />
              <p>Your cart is empty.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {items.map((item) => (
                <div key={item.id} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {item.thumbnail ? (
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{ width: 56, height: 56, borderRadius: 6, background: '#f1f1f1', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#777' }}>${item.unit_price.toFixed(2)} each</div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                      <button
                        onClick={() => (item.quantity <= 1 ? removeItem(item.id) : updateItem(item.id, item.quantity - 1))}
                        aria-label="Decrease quantity"
                        style={qtyBtnStyle}
                      >
                        <Minus size={13} />
                      </button>
                      <span style={{ minWidth: 18, textAlign: 'center', fontWeight: 600, fontSize: '0.9rem' }}>{item.quantity}</span>
                      <button onClick={() => updateItem(item.id, item.quantity + 1)} aria-label="Increase quantity" style={qtyBtnStyle}>
                        <Plus size={13} />
                      </button>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>${(item.unit_price * item.quantity).toFixed(2)}</div>
                    <button
                      onClick={() => removeItem(item.id)}
                      aria-label="Remove item"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: '4px 0 0', lineHeight: 0 }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div style={{ borderTop: '1px solid #eee', padding: '16px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginBottom: 4 }}>
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <p style={{ margin: '0 0 12px', fontSize: '0.78rem', color: '#999' }}>Shipping calculated at checkout</p>
            <button onClick={() => goTo('/checkout')} style={primaryBtnStyle}>
              Checkout
            </button>
            <button onClick={() => goTo('/cart')} style={secondaryBtnStyle}>
              View cart
            </button>
            <button onClick={continueShopping} style={linkBtnStyle}>
              Continue shopping
            </button>
          </div>
        )}
      </aside>
    </>
  );
}

const qtyBtnStyle: React.CSSProperties = {
  width: 26,
  height: 26,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #ddd',
  borderRadius: 4,
  background: '#fff',
  cursor: 'pointer',
};

const primaryBtnStyle: React.CSSProperties = {
  width: '100%',
  padding: 13,
  background: '#2c3e50',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryBtnStyle: React.CSSProperties = {
  width: '100%',
  padding: 11,
  marginTop: 8,
  background: '#fff',
  color: '#2c3e50',
  border: '1px solid #2c3e50',
  borderRadius: 8,
  fontSize: '0.95rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const linkBtnStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 0 0',
  background: 'none',
  color: '#888',
  border: 'none',
  fontSize: '0.85rem',
  cursor: 'pointer',
};
